import React, { useState, useEffect } from 'react';
import {
  DndContext,
  DragEndEvent,
  DragOverlay,
  DragStartEvent,
  closestCorners,
  PointerSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import {
  SortableContext,
  horizontalListSortingStrategy,
} from '@dnd-kit/sortable';
import { arrayMove } from '@dnd-kit/sortable';
import { message, Badge } from 'antd';
import type {
  KanbanBoard as KanbanBoardType,
  KanbanBucket as KanbanBucketType,
} from '@/api/types/kanban.types';
import { useUserTimezone } from '@/hooks/useUserTimezone';
import { KanbanBucket } from './KanbanBucket';
import { kanbanService } from '@/api/services/kanban.service';
import './KanbanBoard.css';

interface KanbanBoardProps {
  board: KanbanBoardType;
  onBoardUpdate?: () => void;
}

/**
 * Kanban Board Component
 * Phase 4: Drag and drop support for buckets and items
 */
export const KanbanBoard: React.FC<KanbanBoardProps> = ({
  board,
  onBoardUpdate,
}) => {
  const { formatDate } = useUserTimezone();
  const [buckets, setBuckets] = useState(board.buckets);
  const [activeBucketId, setActiveBucketId] = useState<string | null>(null);
  const [activeItemId, setActiveItemId] = useState<string | null>(null);
  const [overId, setOverId] = useState<string | null>(null);

  // Update buckets when board prop changes (e.g., when navigating between boards)
  useEffect(() => {
    setBuckets(board.buckets);
  }, [board]);

  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 8, // 8px movement required before drag starts
      },
    })
  );

  const handleDragStart = (event: DragStartEvent) => {
    const { active } = event;
    const activeId = active.id as string;

    // Check if dragging a bucket or an item
    if (activeId.startsWith('bucket-')) {
      setActiveBucketId(activeId.replace('bucket-', ''));
    } else if (activeId.startsWith('item-')) {
      setActiveItemId(activeId.replace('item-', ''));
    }
  };

  const handleDragOver = (event: any) => {
    const { over } = event;
    setOverId(over ? (over.id as string) : null);
  };

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;

    setActiveBucketId(null);
    setActiveItemId(null);
    setOverId(null);

    if (!over) return;

    const activeId = active.id as string;
    const overId = over.id as string;

    // Handle bucket reordering
    if (activeId.startsWith('bucket-') && overId.startsWith('bucket-')) {
      const oldIndex = buckets.findIndex((b) => `bucket-${b.id}` === activeId);
      const newIndex = buckets.findIndex((b) => `bucket-${b.id}` === overId);

      if (oldIndex !== newIndex) {
        const newBuckets = arrayMove(buckets, oldIndex, newIndex);
        setBuckets(newBuckets);
        message.success('Bucket reordered (frontend only)');

        // TODO: API call will be added later
        // await kanbanService.reorderBucket(bucketId, newIndex);
      }
      return;
    }

    // Handle item movement
    if (activeId.startsWith('item-')) {
      const itemId = activeId.replace('item-', '');

      // Determine target bucket ID
      let targetBucketId: string | null = null;

      if (overId.startsWith('bucket-')) {
        // Dropped directly on bucket
        targetBucketId = overId.replace('bucket-', '');
      } else if (overId.startsWith('item-')) {
        // Dropped on another item - find which bucket that item belongs to
        const overItemId = overId.replace('item-', '');
        for (const bucket of buckets) {
          if (bucket.items.some((item) => item.id === overItemId)) {
            targetBucketId = bucket.id;
            break;
          }
        }
      }

      if (!targetBucketId) return;

      // Find source bucket and item
      let sourceItem: any = null;
      let sourceBucketId: string | null = null;

      const updatedBuckets = buckets.map((bucket) => {
        const itemIndex = bucket.items.findIndex((item) => item.id === itemId);
        if (itemIndex !== -1) {
          sourceItem = bucket.items[itemIndex];
          sourceBucketId = bucket.id;
          // Remove item from source bucket
          return {
            ...bucket,
            items: bucket.items.filter((item) => item.id !== itemId),
          };
        }
        return bucket;
      });

      if (!sourceItem || !sourceBucketId) return;

      // If moving within same bucket, just reorder
      if (sourceBucketId === targetBucketId) {
        // Handle reordering within same bucket
        const bucket = buckets.find((b) => b.id === sourceBucketId);
        if (!bucket) return;

        const oldIndex = bucket.items.findIndex((item) => item.id === itemId);
        let newIndex = oldIndex;

        if (overId.startsWith('item-')) {
          const overItemId = overId.replace('item-', '');
          newIndex = bucket.items.findIndex((item) => item.id === overItemId);
        }

        if (oldIndex !== newIndex) {
          const finalBuckets = buckets.map((b) => {
            if (b.id === sourceBucketId) {
              const newItems = arrayMove(b.items, oldIndex, newIndex);
              return { ...b, items: newItems };
            }
            return b;
          });
          setBuckets(finalBuckets);
          message.success('Ticket reordered (frontend only)');
        }
        return;
      }

      // Add item to target bucket at the correct position
      const finalBuckets = updatedBuckets.map((bucket) => {
        if (bucket.id === targetBucketId) {
          // Determine insertion position
          let insertIndex = bucket.items.length; // Default: end of list

          if (overId.startsWith('item-')) {
            // Dropped on another item - insert at that position
            const overItemId = overId.replace('item-', '');
            const overIndex = bucket.items.findIndex(
              (item) => item.id === overItemId
            );
            if (overIndex !== -1) {
              insertIndex = overIndex;
            }
          }

          // Insert item at the correct position
          const newItems = [...bucket.items];
          newItems.splice(insertIndex, 0, sourceItem);

          return {
            ...bucket,
            items: newItems,
          };
        }
        return bucket;
      });

      setBuckets(finalBuckets);
      message.success('Ticket moved (frontend only)');

      // TODO: API call will be added later
      // await kanbanService.moveItem(itemId, targetBucketId);
    }
  };

  const bucketIds = buckets.map((bucket) => `bucket-${bucket.id}`);

  return (
    <DndContext
      sensors={sensors}
      collisionDetection={closestCorners}
      onDragStart={handleDragStart}
      onDragOver={handleDragOver}
      onDragEnd={handleDragEnd}
    >
      <div className="kanban-board">
        <div className="kanban-board-header">
          <h2>{board.title}</h2>
          {board.description && <p>{board.description}</p>}
        </div>
        <div className="kanban-board-content">
          <SortableContext
            items={bucketIds}
            strategy={horizontalListSortingStrategy}
          >
            {buckets.map((bucket) => (
              <KanbanBucket
                key={bucket.id}
                bucket={bucket}
                isDraggingBucket={activeBucketId === bucket.id}
                activeItemId={activeItemId}
                overId={overId}
              />
            ))}
          </SortableContext>
          <div className="kanban-add-bucket">
            <button className="kanban-add-bucket-btn">
              + Dodaj novi bucket
            </button>
          </div>
        </div>
      </div>
      <DragOverlay>
        {activeBucketId &&
          (() => {
            const bucket = buckets.find((b) => b.id === activeBucketId);
            if (!bucket) return null;
            return (
              <div className="kanban-bucket-drag-preview">
                <div className="kanban-bucket-header">
                  <h3 className="kanban-bucket-title">
                    {bucket.title}
                    <Badge
                      count={bucket.items.length}
                      showZero
                      style={{ marginLeft: '8px', backgroundColor: '#52c41a' }}
                    />
                  </h3>
                </div>
                <div className="kanban-bucket-body">
                  <div className="kanban-bucket-items">
                    {bucket.items.slice(0, 3).map((item) => (
                      <div key={item.id} className="kanban-item">
                        <div className="kanban-item-title">{item.title}</div>
                        <div className="kanban-item-footer">
                          <div className="kanban-item-id">
                            {formatDate(item.created_at, 'DD.MM')}{' '}
                            <span className="ticket-id">
                              {item.item_data.unique_id}
                            </span>
                          </div>
                        </div>
                      </div>
                    ))}
                    {bucket.items.length > 3 && (
                      <div className="kanban-items-more">
                        +{bucket.items.length - 3} more items
                      </div>
                    )}
                  </div>
                </div>
              </div>
            );
          })()}
        {activeItemId &&
          (() => {
            // Find the item being dragged
            let activeItem = null;
            for (const bucket of buckets) {
              const item = bucket.items.find((i) => i.id === activeItemId);
              if (item) {
                activeItem = item;
                break;
              }
            }
            if (!activeItem) return null;
            return (
              <div className="kanban-item kanban-item-drag-preview">
                <div className="kanban-item-title">{activeItem.title}</div>
                <div className="kanban-item-footer">
                  <div className="kanban-item-id">
                    {formatDate(activeItem.created_at, 'DD.MM')}{' '}
                    <span className="ticket-id">
                      {activeItem.item_data.unique_id}
                    </span>
                  </div>
                </div>
              </div>
            );
          })()}
      </DragOverlay>
    </DndContext>
  );
};
